import dayjs from 'dayjs'
import { DATE_TIME_FORMAT } from 'utils/constants/datetime'

/** Backend `type` on lookup / warning items — drives icon & dialog variant */
export enum QrLookupPresentationType {
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
}

export type QrLookupWarningItem = {
  code: string
  bookedTimeslot?: string
  scannedAtTimeslot?: string
  type: string
  title: string
  subtitle: string
  message: string
}

export type QrCustomerInfo = {
  name: string
  furigana?: string
  email: string
  phoneNumber: string
  bookedTimeslot?: string
  guestCount?: number
  checkedInAt?: string
}

/** Lookup success body (`type: success`) */
export type QrLookupReservationData = {
  type: string
  reservationId: string
  name: string
  furigana?: string
  email: string
  phoneNumber: string
  timeslotId: string
  timeslotStart: string
  timeslotEnd: string
  guestCount: number
  checkedInGuests?: number
  eventCheckedInCount?: number
  reservationStatus?: string
  tokenStatus?: string
  bookedTimeslot?: string
  customer?: QrCustomerInfo
  warnings?: QrLookupWarningItem[]
}

/** Lookup declined (`rejected: true`, `type: error`) */
export type QrLookupRejectedPayload = {
  rejected: true
  reason: string
  type: string
  title: string
  subtitle: string
  message: string
  checkedInAt?: string
  bookedTimeslot?: string
  timeslotStart?: string
  timeslotEnd?: string
  guestCount?: number
  customer?: QrCustomerInfo
}

export function isLookupRejected(
  d: QrLookupReservationData | QrLookupRejectedPayload
): d is QrLookupRejectedPayload {
  return (d as QrLookupRejectedPayload).rejected === true
}

export type ScanRegistrationDetail = {
  _id: string
  name: string
  furigana: string
  email: string
  phoneNumber: string
  guest: number
  timeslotLabel: string
  timeslotId?: string
  checkedInGuests?: number
  reservationStatus?: string
}

/** Normalized lookup result for UI */
export type QrLookupResult = {
  presentationType: QrLookupPresentationType
  reason?: string
  title: string
  subtitle: string
  message: string
  detail?: ScanRegistrationDetail
  customer?: QrCustomerInfo
  guestCount?: number
  checkedInAt?: string
  showCheckIn: boolean
}

/** Response `data` from POST /api/v1/reservation/qr/confirm — success (`type: success`) */
export type QrConfirmReservationData = {
  type: string
  reservationId: string
  name: string
  furigana?: string
  email: string
  phoneNumber: string
  timeslotId: string
  timeslotStart: string
  timeslotEnd: string
  guestCount: number
  eventCheckedInCount?: number
  reservationStatus: string
  tokenStatus: string
  bookedTimeslot?: string
  customer?: QrCustomerInfo
  warnings?: QrLookupWarningItem[]
}

/** Confirm declined (`rejected: true`) */
export type QrConfirmRejectedPayload = {
  rejected: true
  reason: string
  type: string
  title: string
  subtitle: string
  message: string
  bookedTimeslot?: string
  scannedAtTimeslot?: string
  checkedInAt?: string
  guestCount?: number
  customer?: QrCustomerInfo
}

export function isConfirmRejected(
  d: QrConfirmReservationData | QrConfirmRejectedPayload
): d is QrConfirmRejectedPayload {
  return (d as QrConfirmRejectedPayload).rejected === true
}

export type QrConfirmOkResult = { kind: 'ok'; data: QrConfirmReservationData }
export type QrConfirmRejectedResult = {
  kind: 'rejected'
  result: QrLookupResult
}
export type QrConfirmResult = QrConfirmOkResult | QrConfirmRejectedResult

export function mapQrLookupToDetail(
  data: QrLookupReservationData
): ScanRegistrationDetail {
  const customer = data.customer

  return {
    _id: data.reservationId,
    name: customer?.name ?? data.name,
    furigana: customer?.furigana ?? data.furigana ?? '',
    email: customer?.email ?? data.email,
    phoneNumber: customer?.phoneNumber ?? data.phoneNumber,
    guest: customer?.guestCount ?? data.guestCount,
    timeslotLabel:
      customer?.bookedTimeslot ??
      data.bookedTimeslot ??
      `${dayjs(data.timeslotStart).format(DATE_TIME_FORMAT.HOUR)} - ${dayjs(data.timeslotEnd).format(DATE_TIME_FORMAT.HOUR)}`,
    timeslotId: data.timeslotId,
    checkedInGuests: data.checkedInGuests,
    reservationStatus: data.reservationStatus,
  }
}

export function customerFromReservation(
  data: QrLookupReservationData
): QrCustomerInfo {
  if (data.customer) return data.customer

  return {
    name: data.name,
    furigana: data.furigana,
    email: data.email,
    phoneNumber: data.phoneNumber,
    bookedTimeslot: data.bookedTimeslot,
    guestCount: data.guestCount,
  }
}
